'use client';

import './sheet.scss';

import { forwardRef } from 'react';

import {
  ButtonStyle,
  ButtonStyleProps,
} from '../../atoms/button-style/button-style';
import { cn } from '../../utils/helpers';
import {
  Modal,
  ModalClose,
  ModalCloseButton,
  ModalContent,
  ModalContentProps,
  ModalProps,
  ModalTitle,
  ModalTrigger,
  ModalTriggerProps,
} from '../modal/modal';

export type SheetProps = ModalProps;

export const Sheet = Modal;

/**
 * Sheet content.
 */
export type SheetContentProps = ModalContentProps & {
  title?: string;
  footer?: React.ReactNode;
};

// TODO: Add functionality for "isNarrow" prop (where max-width of content changes): essentially default to the wide version (768px) and have a isNarrow or something property which would drive the more narrow constraint (500px). Note that this does not affect the Sheet width, just the max width of the content inside the sheet.
export const SheetContent = forwardRef<HTMLDivElement, SheetContentProps>(
  ({ title, footer, style, ...props }, ref) => {
    return (
      <ModalContent
        ref={ref}
        aria-describedby={undefined}
        className="Sheet__content w-full flex flex-col"
        style={{
          ...style,
        }}
        {...props}
      >
        {/* Header */}
        <header className="flex flex-shrink-0 justify-between items-center w-full mx-auto p-comfy-sm md:px-comfy-xl lg:py-comfy-md lg:max-w-md">
          <ModalTitle className="heading-xs lg:heading-sm">{title}</ModalTitle>
          <ModalCloseButton
            name="Sheet"
            className="Sheet__close mr-snug-lg lg:mr-comfy-xs"
          />
        </header>

        {/* Body with border lines */}
        <div
          className={cn('Sheet__body flex-1 overflow-auto w-full', {
            'Sheet__body--hasFooter': !!footer,
          })}
        >
          {/* Max-width container here, padding added here to allow divider lines to take up full width */}
          <div className="max-w-full lg:max-w-md mx-auto py-comfy-lg px-comfy-sm md:p-comfy-xl">
            {props.children}
          </div>
        </div>

        {/* Footer */}
        <div className="Sheet__footer flex-shrink-0 px-comfy-sm py-comfy-xs mx-auto md:px-comfy-xl w-full lg:max-w-md">
          {footer && footer}
        </div>
      </ModalContent>
    );
  },
);
/**
 * Sheet trigger.
 */
export type SheetTriggerProps = ModalTriggerProps;
export const SheetTrigger = ModalTrigger;

/**
 * Sheet close.
 */
export const SheetClose = ModalClose;

export interface SheetActionsProps {
  primaryBtn?: ButtonStyleProps;
  secondaryBtn?: ButtonStyleProps;
}

export const SheetActions: React.FC<SheetActionsProps> = ({
  primaryBtn,
  secondaryBtn,
}) => {
  return (
    <div className="flex items-center justify-center gap-comfy-xs md:gap-comfy-sm">
      {secondaryBtn && (
        <SheetClose asChild>
          <ButtonStyle
            size="lg"
            purpose="neutral"
            variant="secondary"
            fullWidth
            {...secondaryBtn}
          />
        </SheetClose>
      )}
      {primaryBtn && (
        <ButtonStyle size="lg" variant="primary" fullWidth {...primaryBtn} />
      )}
    </div>
  );
};
